import { TableNamed } from './table-patterns';
import { Permission, User } from './Users';
import { Athlete } from './athletes.model';
import { Club } from './clubs.model';
import { Contributor } from '../features/contributor/models/contributor.model';
import { League } from './leagues.model';
import { Referee } from './referee.model';
import { Relation } from './relations.model';
import { ColorHelper } from '../core/helpers/color.helpers';
import { FinancialOptions } from './financial-options.model';
import { School } from '../features/school/models/school.model';
import * as moment from 'moment';
import { Indexable } from '../core/models/indexable';
import { StringHelper } from '../core/helpers/string.helperts';
import { DocumentTypes } from '../features/documents/models/document.models';

// tslint:disable: variable-name

export type EntityStatus = 'A' | 'B' | 'D';
export const ENTITY_STATUS: Indexable<EntityStatus, string> = {
  A: 'Ativo',
  B: 'Bloqueado',
  D: 'Excluido'
};

export type EntityNameFormat = 'name' | 'nickname' | 'surname' | 'name-surname';
export const ENTITY_NAME_FORMAT: Indexable<EntityNameFormat, string> = {
  name: 'Nome',
  nickname: 'Apelido',
  surname: 'Sobrenome',
  'name-surname': 'Nome e Sobrenome'
};

export type EntityNameCapitalization = 'auto' | 'none' | 'upper' | 'lower';
export const ENTITY_NAME_CAPITALIZATION: Indexable<EntityNameCapitalization, string> = {
  auto: 'Padrão',
  upper: 'Maiusculo',
  lower: 'Minusculo',
  none: 'Nenhum'
};

export interface EntityNameFormatOptions {
  format?: EntityNameFormat;
  default?: string;
  capitalize?: EntityNameCapitalization;
}

export class EntityInternalOptions {
  financial: FinancialOptions;

  constructor(o?: EntityInternalOptions) {
    if (!o) {
      o = {} as any;
    }
    if (!o.financial || !(o.financial instanceof FinancialOptions)) {
      o.financial = new FinancialOptions(o.financial);
    }

    Object.assign(this, o);
  }
}

export class Entity extends TableNamed {
  document: string;
  nickname: string;
  email: string;
  photo_url: string;
  is_company: boolean;
  gender: 'M' | 'F';
  birth?: string;
  country: string;
  state?: string;
  city?: string;
  street?: string;
  number?: string;
  complement?: string;
  foreigner: boolean;
  document_foreigner: string;
  documents_ids?:DocumentTypes;
  zip_code?: string;
  neighborhood?: string;
  roles?: Permission[];
  born_city?: string;
  scholarity?: string;
  punishiment?:string;
  preferences?: {
    brand_color?: string,
    brand_contrast?: string,
    theme_active?: boolean,
    account_type?: string,
    sport_id?: number,
    profile_completed?: boolean,
    usability?: { [index: string]: any }
  };
  transfer_id?: string;
  activities?: string;
  entity_uid: string;
  invite_available: boolean;
  validated?: boolean;
  validation_id?: number;
  validator_id?: number;
  revalidate?: boolean;
  validation_expires?: string;
  internal_options?: EntityInternalOptions;
  self_user?: number;
  relation_code?: string;
  access_blocked?: string;
  user_exclusion_id?: number;
  situation: EntityStatus;
  mobile_phone?: string;
  contact_phone?: string;
  residential_phone?: string;

  constructor(o: Partial<Entity>, normalize?: { int?: string[], dates?: string[] }) {
    super(o, normalize);
    if (this.preferences) {
      if (this.preferences.sport_id && typeof this.preferences.sport_id !== 'number') {
        this.preferences.sport_id = parseInt(this.preferences.sport_id);
      }
    }

    this.internal_options = new EntityInternalOptions(o ? o.internal_options : undefined);
  }

  static getEntityTypeName(entityType: TableName, plural = false) {
    let ret = '';

    switch (entityType) {
      case 'athletes': ret = 'Atleta'; break;
      case 'clubs': ret = 'Clube'; break;
      case 'contributors': ret = 'Comissão Técnica'; break;
      case 'leagues': ret = 'Liga'; break;
      case 'referees': ret = 'Árbitro'; break;
      case 'users': ret = 'Usuário'; break;
      case 'stadiums': ret = 'Ginásio'; break;
      case 'championships': ret = 'Campeonato'; break;
    }

    if (plural && entityType !== 'contributors') {
      ret += 's';
    }

    return ret;
  }

  static yearAge(birth: string, baseYear?: number) {
    const _baseYear = baseYear || new Date().getFullYear();

    return _baseYear - new Date(birth).getFullYear();
  }

  static realAge(birth: string) {
    const ageDifMs = Date.now() - new Date(birth).getTime();
    const ageDate = new Date(ageDifMs); // miliseconds from epoch
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  static getAge(birth: moment.Moment) {
    return moment().diff(birth, 'years');
  }

  static getFormattedName(entity: Entity, options?: EntityNameFormatOptions) {
    if (!entity) {
      if (options.default) {
        return options.default;
      }

      return '';
    }

    if (!options) {
      options = {
        capitalize: 'auto',
        format: 'name',
      }
    }

    let res = entity.name;
    if (options.format?.inArray(['name', 'nickname'])) {
      res = entity[options.format];
    }

    const names = entity.name.trim().split(' ').filter(item => item != '' && item != null);
    if (options.format == 'name-surname') {
      res = names[0] + ' ' + names[names.length - 1];
    }
    if (options.format == 'surname') {
      res = names[names.length - 1];
    }

    if (options.capitalize) {
      if (options.capitalize == 'lower') {
        res = res.toLowerCase();
      } else if (options.capitalize == 'upper') {
        res = res.toUpperCase();
      } else if (options.capitalize == 'auto') {
        res = res.capitalize();
      }
    }
    return res;
  }

  static formatDocument(entity: Entity, masked = false) {
    if (entity.is_company) {
      return StringHelper.asCNPJ(entity.document, masked);
    } else {
      return StringHelper.asCPF(entity.document, masked);
    }
  }

  getBrandColor() {
    if (this.preferences && this.preferences.brand_color) {
      return this.preferences.brand_color;
    }
    return '#3f51b5';
  }

  getBrandContrastColor(baseColor?: string) {
    if (this.preferences && this.preferences.brand_contrast) {
      return this.preferences.brand_contrast;
    }
    if (baseColor) { return ColorHelper.invertColor(baseColor); }
    return 'white';
  }

  getEntityTypeName(plural = false) {
    return Entity.getEntityTypeName(this.source_name, plural);
  }

  documentAsCPF() {
    return StringHelper.asCPF(this.document);
  }

  shortName() {
    return Entity.getFormattedName(this, { format: 'name-surname' })
  }

  getRealAge(){
    return Entity.realAge(this.birth);
  }
}
export class EntityRelation extends Entity {
  relation: Relation;
}


export type EntityType = 'athletes' | 'clubs' | 'contributors' | 'leagues' | 'referees' | 'users' | 'stadiums' | 'schools';
export type EntityClass = Athlete | Club | Contributor | League | Referee | User | Entity | School;
export type TableName = EntityType | 'championships' | 'phases';
